import { useCallback, useEffect, useRef, useState } from "react";

const MARGEM = 8;

/** Mantém o elemento inteiro dentro da janela do navegador. */
export const limitarNaTela = (pos, largura, altura) => {
  const maxX = Math.max(MARGEM, window.innerWidth - largura - MARGEM);
  const maxY = Math.max(MARGEM, window.innerHeight - altura - MARGEM);
  return {
    x: Math.min(Math.max(MARGEM, pos.x), maxX),
    y: Math.min(Math.max(MARGEM, pos.y), maxY),
  };
};

/**
 * Arrastar com o mouse (ou dedo) sem sair da tela.
 *
 * Durante o arraste a posição fica SÓ aqui dentro (estado local, 1 atualização
 * por quadro de tela). O resto do sistema só fica sabendo quando solta o mouse
 * (onMover). Antes cada pixel atualizava o contexto inteiro e a tela travava.
 *
 * - Mexeu menos de 4px: conta como clique (onClique).
 * - Clicar num botão/campo dentro da área de arrastar não arrasta.
 * - Use o "pos" devolvido para desenhar (left/top).
 */
export default function useArrastavel({ pos, largura, altura, onMover, onClique }) {
  const [aoVivo, setAoVivo] = useState(null);
  const quadro = useRef(null);
  const ultimo = useRef(null);

  const onPointerDown = useCallback(
    (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      if (e.target.closest && e.target.closest("button, input, select, textarea, a")) return;
      e.preventDefault();

      const inicio = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y };
      let moveu = false;
      const janela = e.view || window; // funciona também dentro da janela "sempre no topo"

      const mover = (ev) => {
        const dx = ev.clientX - inicio.x;
        const dy = ev.clientY - inicio.y;
        if (!moveu && Math.hypot(dx, dy) < 4) return;
        moveu = true;
        document.body.style.userSelect = "none";
        ultimo.current = limitarNaTela({ x: inicio.px + dx, y: inicio.py + dy }, largura, altura);
        if (quadro.current) return;
        quadro.current = requestAnimationFrame(() => {
          quadro.current = null;
          setAoVivo(ultimo.current);
        });
      };
      const soltar = (ev) => {
        janela.removeEventListener("pointermove", mover);
        janela.removeEventListener("pointerup", soltar);
        document.body.style.userSelect = "";
        if (quadro.current) {
          cancelAnimationFrame(quadro.current);
          quadro.current = null;
        }
        if (moveu && ultimo.current) onMover(ultimo.current);
        setAoVivo(null);
        if (!moveu && onClique) onClique(ev);
      };
      janela.addEventListener("pointermove", mover);
      janela.addEventListener("pointerup", soltar);
    },
    [pos.x, pos.y, largura, altura, onMover, onClique]
  );

  // Janela do navegador diminuiu: puxa de volta para dentro.
  useEffect(() => {
    const aoRedimensionar = () => {
      const p = limitarNaTela(pos, largura, altura);
      if (p.x !== pos.x || p.y !== pos.y) onMover(p);
    };
    window.addEventListener("resize", aoRedimensionar);
    return () => window.removeEventListener("resize", aoRedimensionar);
  }, [pos, largura, altura, onMover]);

  return {
    onPointerDown,
    pos: aoVivo || pos,
    arrastando: !!aoVivo,
    style: { touchAction: "none", willChange: aoVivo ? "left, top" : undefined },
  };
}
